import { Fragment, useEffect, useMemo, useState } from "react";

import { Box, Grid, IconButton, Paper, Switch, TableCell, TableRow, Tooltip, Typography, Stack } from "@mui/material";
import { Edit, Delete, Lens } from "@material-ui/icons";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { convertDateTimeIntoTimezone, readablePeriod } from "../../../utils/util";
import { accessNetworkSourceLabel, Action, actionTypeLabel, Device, floorActionOutcome, formatWanScope, isFloorQuota, Quota, quotaStatusLabel, suppressionReasonLabel } from "../types";

import { UsageLineRep } from "../UsageLineRep";
import moment from "moment-timezone";
import _ from "lodash";
import Warning from "../../../asset/image/warning.svg"
import { MMDDYYYYHMM_DATE_FORMAT_24_HRS, MMDDYYYYHMMSS_DATE_FORMAT_24_HRS } from "../../../utils/constants";

// ids carry the existing ceiling dot colours; floors override where their colour differs
const STATUS_DOT_IDS: { [status: string]: string } = {
    'active': 'usageBlueDotSymbol',
    'alerting': 'usageYellowDotSymbol',
    'exceeded': 'usageRedDotSymbol',
}

const FLOOR_STATUS_DOT_CLASSES: { [status: string]: string } = {
    'active': 'quota_management--status_dot quota_management--status_dot--above_minimum',
    'unknown': 'quota_management--status_dot quota_management--status_dot--not_evaluated',
}

// most recently fired, so a ladder that escalated names its last step
const lastFiredAction = (actions: Action[] | undefined): Action | undefined =>
    _.maxBy((actions ?? []).filter(a => !a.revert && a.executed && a.executed_at), a => a.executed_at as string);

const floorStatusSubLine = (quota: Quota, userTimezone: string): string => {
    if (quota.status === 'unknown') return suppressionReasonLabel(quota.suppression_reason);
    if (quota.status !== 'exceeded') return '';
    // P1: the status stays exceeded while recovering, the progress lives here
    if ((quota.healthy_window_count ?? 0) > 0) return `Recovering ${quota.healthy_window_count}/${quota.recovery_windows ?? 2}`;
    const fired = lastFiredAction(quota.actions);
    if (!fired) return floorActionOutcome(quota) === 'withheld' ? 'Link is moving again' : 'No action ran';
    return `${actionTypeLabel(fired.type)} · ${convertDateTimeIntoTimezone(fired.executed_at, userTimezone, MMDDYYYYHMM_DATE_FORMAT_24_HRS)}`;
}

interface EditableQuotaRowProps {
    userTimezone: string;
    device: Device;
    quota: Quota;
    gettingCurrentUsage: boolean;
    onEdit: (quota: Quota) => void;
    onDelete: (quota: Quota) => void;
    onCopy: (quota: Quota) => void;
    onToggleDisable: (quota: Quota) => void;
    isReadOnlyUser: boolean;
    devices: any
}

export function EditableQuotaRow(props: EditableQuotaRowProps) {
    const { userTimezone, quota, devices, onEdit, onCopy, onDelete, onToggleDisable, isReadOnlyUser, device } = props;
    const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(quota != null ? devices.find(d => d.id === quota.device_id) : undefined);
    const [deviceInterface, setDeviceInterface] = useState<string[]>(quota != null && quota?.wan_links ? quota.wan_links : []);

    const handleEdit = () => {
        onEdit(quota);
    }

    const handleCopy = () => {
        onCopy(quota);
    }

    const handleDelete = () => {
        onDelete(quota);
    }

    const handleToggleDisable = () => {
        onToggleDisable({...quota, disabled: !quota.disabled});
    }

    const showDualUsageWarning = () => {
        if (!quota.wan_links || !quota.starlink_quota || !quota.starlink_quota.enabled) return null;
        return quota.wan_links?.length > 0 && quota.wan_links?.some(di => device.interfaces.find(i => i.alias == di && i.wan_type == 'starlink')) ? <Tooltip title="Double counting of starlink usage">
            <Typography className="wan-hover"><img src={Warning} alt="warning"></img></Typography>
        </Tooltip> : null
    }

    useEffect(() => {
        if (devices?.length > 0) {
            setSelectedDevice(quota != null ? devices.find(d => d.id === quota.device_id) : undefined);
            setDeviceInterface(quota != null && quota.wan_links ? quota.wan_links : []);
        }
    }, [devices])

    const deviceInterfaces = useMemo(() => {
        let device = devices.find(d => d.id === selectedDevice?.id);
        if (device) {
            return device.interfaces;
        }
        return [];
    }, [selectedDevice])

    const invalidDeviceInterfaces = useMemo(() => {
        if (quota?.access_level === 'read') return []
        return deviceInterface.filter(di => deviceInterfaces.findIndex(d => d.alias === di) === -1)
    }, [deviceInterface, deviceInterfaces, quota?.access_level])

    const isFloor = isFloorQuota(quota);
    const statusLabel = quotaStatusLabel(quota);
    const statusDotId = STATUS_DOT_IDS[quota?.status ?? ''];
    const statusDotClass = isFloor ? FLOOR_STATUS_DOT_CLASSES[quota?.status ?? ''] : undefined;
    const statusSubLine = isFloor ? floorStatusSubLine(quota, userTimezone) : '';

    return (
        <TableRow key={quota.id} className="row-parent">
            <TableCell>
                {quota.site_name}
            </TableCell>
            <TableCell>
                {quota.device_name}
            </TableCell>
            <TableCell className={quota.starlink_quota?.service_line_numbers?.length !== undefined && quota.starlink_quota?.service_line_numbers?.length > 0 ? "wan-hover-parent" : "wan-hover"}><Stack spacing={1} alignItems="left">
                {quota?.wan_links?.length && quota?.wan_links?.length > 0 ? <Box component="span"><Typography variant="caption" color="text.secondary">WAN INTERFACES: </Typography>{quota?.wan_links?.join(', ')}</Box> : null}
                {quota?.access_networks_name?.length && quota?.access_networks_name?.length > 0 ? <Box component="span"><Typography variant="caption" color="text.secondary" >{accessNetworkSourceLabel(quota?.captive_usage_type).toUpperCase()}: </Typography>{quota?.access_networks_name?.join(', ')}</Box> : null}
                {quota?.access_network_wan_scope?.length && quota?.access_network_wan_scope?.length > 0 ? <Box component="span"><Tooltip title="Only this access-network traffic that used the selected WAN types is counted"><Typography variant="caption" color="text.secondary"> WAN TYPES: </Typography></Tooltip>{formatWanScope(quota?.access_network_wan_scope)}</Box> : null}
                {quota?.starlink_quota?.service_line_numbers?.length && quota?.starlink_quota?.service_line_numbers?.length > 0 ? <Box component="span"><Typography variant="caption" color="text.secondary">STARLINK: </Typography>{quota?.starlink_quota?.service_line_numbers?.join(', ')}</Box> : null}
            </Stack></TableCell>
            <TableCell>
                {(quota?.quota_type || 'USER').toUpperCase()}
            </TableCell>
            <TableCell>
                {isFloor
                    ? <span className="quota_management--dashboard--rule quota_management--dashboard--rule--floor">▼ MIN</span>
                    : <span className="quota_management--dashboard--rule quota_management--dashboard--rule--ceiling">▲ MAX</span>}
            </TableCell>
            <TableCell>
                {isFloor ? 'Rolling' : convertDateTimeIntoTimezone(quota.current_quota_start_time, userTimezone, MMDDYYYYHMMSS_DATE_FORMAT_24_HRS)}
            </TableCell>
            <TableCell>
                {readablePeriod(quota.period, quota.period_unit)}
            </TableCell>
            <TableCell>
                <UsageLineRep quota={quota} userTimezone={userTimezone} />
            </TableCell>
            <TableCell className={invalidDeviceInterfaces.length > 0 ? 'tooltip-invalid-interfaces' : ''}>
                {statusLabel && <Grid classes={{ root: 'quota_management--base--flex--align--center' }}>
                    <Lens id={statusDotClass ? undefined : statusDotId} classes={statusDotClass ? { root: statusDotClass } : undefined} />{statusLabel} {invalidDeviceInterfaces.length > 0 && <Tooltip title={`Selected interface (${invalidDeviceInterfaces.join(', ')}) does not exist, please reconfigure the interfaces`}><img src={Warning} alt=''/></Tooltip>}
                </Grid>}
                {statusSubLine && <div className="quota_management--dashboard--status_sub">{statusSubLine}</div>}
            </TableCell>
            {!isReadOnlyUser && <TableCell>
                <Paper elevation={0} className="quota_management--device_summary--actions">
                    <Fragment>
                        <Tooltip title="Edit"><IconButton size='small' onClick={handleEdit}>
                            <Edit />
                        </IconButton></Tooltip>
                        {quota?.access_level !== "read" && <Tooltip title="Copy"><IconButton size='small' onClick={handleCopy}>
                            <ContentCopyIcon />
                        </IconButton></Tooltip>}
                        {quota?.access_level !== "read" && <Tooltip title="Delete"><IconButton size='small' onClick={handleDelete}>
                            <Delete />
                        </IconButton></Tooltip>}
                        {quota?.access_level !== "read" && <Tooltip title="Enable / Disable">
                            <Switch
                                size="small"
                                checked={!quota.disabled}
                                color="primary"
                                onChange={handleToggleDisable}
                            />
                        </Tooltip>}
                    </Fragment>
                </Paper>
            </TableCell>}
        </TableRow>
    )
}